import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap, map } from 'rxjs/operators';
import { BaseAsset } from '../models/assets/BaseAsset';


import { TextAsset } from '../models/assets/TextAsset';
import { MediaAsset } from '../models/assets/MediaAsset';

import { HotSpotAsset } from '../models/assets/HotSpotAsset';
import { Model3DAsset } from '../models/assets/Model3DAsset';
import { QuestionAsset } from '../models/assets/QuestionAsset';
import { QuestionSetAsset } from '../models/assets/QuestionSetAsset';
import { AssetType } from '../models/Assets/AssetType';
import { API_SETTINGS } from './API_SETTINGS';
import { InvisibleAsset } from '../models/assets/InvisibleAsset';

const httpOptions = {
  headers: new HttpHeaders({'Content-Type': 'application/json'})
};

const apiUrlBaseAsset = API_SETTINGS.API_URL + 'BaseAsset';
const apiUrlAssetType = API_SETTINGS.API_URL + 'AssetType';

@Injectable({
  providedIn: 'root'
})
export class ApiAssetService {

  constructor(private http: HttpClient,) { }

  private handleError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
  
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead
  
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  getBaseAssets(): Observable<BaseAsset[]> {
    const url = apiUrlBaseAsset+'/GetBaseAssets';
    return this.http.get<BaseAsset[]>(url).pipe();
  }
  
  getBaseAsset(id: number): Observable<BaseAsset> {
    const url = `${apiUrlBaseAsset}/GetBaseAsset/${id}`;
    return this.http.get<BaseAsset>(url).pipe();
  }

  getAssetTypes(): Observable<AssetType[]> {
    const url = apiUrlAssetType + '/GetAssetTypes';
    return this.http.get<AssetType[]>(url).pipe();
  }

  addAsset(asset: BaseAsset, assetType: string): Observable<BaseAsset> {
    switch(assetType){
      case 'Text':{
        return this.addTextAsset(asset as TextAsset);
      }
      case 'Image':
      case 'Video':
      case 'Audio':{
        return this.addMediaAsset(asset as MediaAsset, assetType);
      }
      case 'HotSpot':{
        return this.addHotSpotAsset(asset as HotSpotAsset);
      }
      case 'Model3D':{
        return this.addModel3DAsset(asset as Model3DAsset);
      }
      case 'Question':{
        return this.addQuestionAsset(asset as QuestionAsset);
      }
      case 'QuestionSet':{
        return this.addQuestionSetAsset(asset as QuestionSetAsset);
      }
      case 'Invisible':{
        return this.addInvisibleAsset(asset as InvisibleAsset);
      }
    }
  }

  addBaseAsset(BaseAsset: BaseAsset): Observable<BaseAsset>{  
    const url = apiUrlBaseAsset+'/PostBaseAsset';
    return this.http.post<BaseAsset>(url, BaseAsset, httpOptions).pipe();
  }

  addTextAsset(TextAsset: TextAsset): Observable<TextAsset>{  
    const url = apiUrlBaseAsset+'/PostTextAsset';
    return this.http.post<TextAsset>(url, TextAsset, httpOptions).pipe();
  }
  
  addMediaAsset(MediaAsset: MediaAsset, assetType): Observable<MediaAsset>{  
    const url = apiUrlBaseAsset+'/Post' + assetType + 'Asset';
    return this.http.post<MediaAsset>(url, MediaAsset, httpOptions).pipe();
  }

  addHotSpotAsset(HotSpotAsset: HotSpotAsset): Observable<HotSpotAsset>{  
    const url = apiUrlBaseAsset+'/PostHotSpotAsset';
    return this.http.post<HotSpotAsset>(url, HotSpotAsset, httpOptions).pipe();
  }

  addModel3DAsset(Model3DAsset: Model3DAsset): Observable<Model3DAsset>{  
    const url = apiUrlBaseAsset+'/PostModel3DAsset';
    return this.http.post<Model3DAsset>(url, Model3DAsset, httpOptions).pipe();
  }

  addQuestionAsset(QuestionAsset: QuestionAsset): Observable<QuestionAsset>{  
    const url = apiUrlBaseAsset+'/PostQuestionAsset';
    return this.http.post<QuestionAsset>(url, QuestionAsset, httpOptions).pipe();
  }

  addQuestionSetAsset(QuestionSetAsset: QuestionSetAsset): Observable<QuestionSetAsset>{  
    const url = apiUrlBaseAsset+'/PostQuestionSetAsset';
    return this.http.post<QuestionSetAsset>(url, QuestionSetAsset, httpOptions).pipe();
  }

  addInvisibleAsset(InvisibleAsset: InvisibleAsset): Observable<InvisibleAsset>{  
    const url = apiUrlBaseAsset+'/PostInvisibleAsset';
    return this.http.post<InvisibleAsset>(url, InvisibleAsset, httpOptions).pipe();
  }

  updateBaseAsset (id: number, BaseAsset: any): Observable<BaseAsset>{
    const url = `${apiUrlBaseAsset}/PutBaseAsset/${id}`;
    return this.http.put(url, BaseAsset, httpOptions).pipe(
      tap((BaseAssetRes: BaseAsset) => true ), //console.log()),
      catchError(this.handleError<BaseAsset>('updateBaseAsset'))
    )
  }


  updateAsset(asset: BaseAsset, assetType: string): Observable<BaseAsset> {
    switch(assetType){
      case 'Text':{
        return this.updateTextAsset(asset.baseAssetId, asset as TextAsset);
      }
      case 'Image':
      case 'Video':
      case 'Audio':{
        return this.updateMediaAsset(asset.baseAssetId, asset as MediaAsset, assetType);
      }
      case 'HotSpot':{
        return this.updateHotSpotAsset(asset.baseAssetId, asset as HotSpotAsset);
      }
      case 'Model3D':{
        return this.updateModel3DAsset(asset.baseAssetId, asset as Model3DAsset);
      }
      case 'Question':{
        return this.updateQuestionAsset(asset.baseAssetId, asset as QuestionAsset);
      }
      case 'QuestionSet':{
        return this.updateQuestionSetAsset(asset.baseAssetId, asset as QuestionSetAsset);
      }
      case 'Invisible':{
        return this.updateInvisibleAsset(asset.baseAssetId, asset as InvisibleAsset);
      }
    }
  }


  updateTextAsset(id: number, TextAsset: any): Observable<TextAsset>{
    const url = `${apiUrlBaseAsset}/PutTextAsset/${id}`;
    return this.http.put(url, TextAsset, httpOptions).pipe(
      tap((TextAssetRes: TextAsset) => true ), //console.log()),
      catchError(this.handleError<TextAsset>('updateTextAsset'))
    )
  }

  updateMediaAsset(id: number, MediaAsset: any, assetType): Observable<MediaAsset>{
    const url = `${apiUrlBaseAsset}/Put` + assetType + `Asset/${id}`;
    return this.http.put(url, MediaAsset, httpOptions).pipe(
      tap((MediaAssetRes: MediaAsset) => true ), //console.log()),
      catchError(this.handleError<MediaAsset>('update' + assetType + 'Asset'))
    )
  }

  updateHotSpotAsset(id: number, HotSpotAsset: any): Observable<HotSpotAsset>{
    const url = `${apiUrlBaseAsset}/PutHotSpotAsset/${id}`;
    return this.http.put(url, HotSpotAsset, httpOptions).pipe(
      tap((HotSpotAssetRes: HotSpotAsset) => true ), //console.log()),
      catchError(this.handleError<HotSpotAsset>('updateHotSpotAsset'))
    )
  }

  updateModel3DAsset(id: number, Model3DAsset: any): Observable<Model3DAsset>{ 
    const url = `${apiUrlBaseAsset}/PutModel3DAsset/${id}`;
    return this.http.put(url, Model3DAsset, httpOptions).pipe(
      tap((Model3DAssetRes: Model3DAsset) => true ), //console.log()),
      catchError(this.handleError<Model3DAsset>('updateModel3DAsset'))
    )
  }

  updateInvisibleAsset(id: number, InvisibleAsset: any): Observable<InvisibleAsset>{ 
    const url = `${apiUrlBaseAsset}/PutInvisibleAsset/${id}`;
    return this.http.put(url, InvisibleAsset, httpOptions).pipe(
      tap((InvisibleAssetRes: InvisibleAsset) => true ), //console.log()),
      catchError(this.handleError<InvisibleAsset>('updateInvisibleAsset'))
    )
  }

  updateQuestionAsset(id: number, QuestionAsset: any): Observable<QuestionAsset>{ 
    const url = `${apiUrlBaseAsset}/PutQuestionAsset/${id}`;
    return this.http.put(url, QuestionAsset, httpOptions).pipe(
     tap((QuestionAssetRes: QuestionAsset) => true ), //console.log()),
     catchError(this.handleError<QuestionAsset>('updateQuestionAsset'))
    )
  }

  updateQuestionSetAsset(id: number, QuestionSetAsset: any): Observable<QuestionSetAsset>{
    const url = `${apiUrlBaseAsset}/PutQuestionSetAsset/${id}`;
    return this.http.put(url, QuestionSetAsset, httpOptions).pipe(
     tap((QuestionSetAssetRes: QuestionSetAsset) => true ), //console.log()),
     catchError(this.handleError<QuestionSetAsset>('updateQuestionSetAsset'))
    )
  }

  setVideoUploadStatusComplete(id: number, MediaAsset: any): Observable<MediaAsset>{
    const url = `${apiUrlBaseAsset}/PutVideoAssetUploadStatusComplete/${id}`;
    return this.http.put(url, MediaAsset, httpOptions).pipe(
      tap((MediaAssetRes: MediaAsset) => true ), //console.log()),
      catchError(this.handleError<MediaAsset>('Set Video Asset Upload Status Complete'))
    )
  }
  
  deleteBaseAsset(id: number): Observable<BaseAsset> {
    const url = `${apiUrlBaseAsset}/DeleteBaseAsset/${id}`;
    return this.http.delete<BaseAsset>(url, httpOptions).pipe();
  }
  
  getBaseAssetsWithBaseAssetIds(BaseAssetIds: number[]): Observable<BaseAsset[]> {
    const url = apiUrlBaseAsset+'/GetBaseAssetsWithBaseAssetIdsAsync';
    return this.http.post<BaseAsset[]>(url, BaseAssetIds, httpOptions).pipe(
      catchError(this.handleError<BaseAsset[]>('getBaseAssetsWithBaseAssetIds')));
  }
  
}
